import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../api/countries/get-countries-list'
import { getNationalitiesList } from '../../../api/countries/get-nationalities-list'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  genderOneLetterMapping,
  toGenderEnumValue
} from '../../../lib/types/enums/common/gender.enum'
import { languageEnumMapping } from '../../../lib/types/enums/languages/language.enum'
import { languageLevelEnumMapping } from '../../../lib/types/enums/languages/language-level.enum'
import { educationLevelEnumMapping } from '../../../lib/types/enums/staff-application/education-level.enum'
import { maritalStatusEnumMapping } from '../../../lib/types/enums/staff-application/marital-status.enum'
import { nationalEducationStatusEnumMapping } from '../../../lib/types/enums/staff-application/national-education-status.enum'
import { schoolLevelTaughtEnumMapping } from '../../../lib/types/enums/staff-application/school-level-taught.enum'
import { staffApplicationLanguageEnumMapping } from '../../../lib/types/enums/staff-application/staff-application-language.enum'
import {
  StaffApplicationType,
  staffApplicationTypeMapping
} from '../../../lib/types/enums/staff-application/staff-application-type.enum'
import { StaffApplication } from '../../../lib/types/models/staff-application/staff-application'
import { getNationalityLabel } from '../../../lib/utils/functions/country.functions'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'
import CountryFlag from '../../elements/country-flag'
import LoadingScreen from '../../elements/loading/loading-screen'
import PeriodBadge from '../../elements/period-badge'

type Props = {
  staffApplication: StaffApplication
}

const StaffApplicationSummary = ({ staffApplication }: Props) => {
  const { t } = useTranslate()
  const { data: countryList } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })
  const { data: nationalitiesList } = useQuery({
    queryKey: ['nationalities-list'],
    queryFn: getNationalitiesList
  })
  const languageMapping = languageEnumMapping()

  const detailsType: 'light' | 'full' =
    staffApplication.type === StaffApplicationType.ANIMATOR ||
    staffApplication.type === StaffApplicationType.AAT
      ? 'light'
      : 'full'

  if (!countryList) return <LoadingScreen />
  return (
    <div className="flex flex-col gap-4">
      <InnerBox header="Informations générales">
        <div className="grid grid-cols-4 gap-7 pl-4 pt-4 items-center">
          <div className="flex flex-col gap-1">
            <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
              Poste
            </Typography>
            <Badge
              variant={staffApplicationTypeMapping[staffApplication.type].variant}
              text={staffApplicationTypeMapping[staffApplication.type].label}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
              Langue
            </Typography>
            <Badge
              variant={staffApplicationLanguageEnumMapping[staffApplication.language].variant}
              text={staffApplicationLanguageEnumMapping[staffApplication.language].label}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
              Date de la candidature
            </Typography>
            <Badge variant="grey" text={DateFormatter.DDMMYYYY(staffApplication.createdAt)} />
          </div>
          <div className="flex flex-col gap-1">
            <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
              Périodes de disponibilité
            </Typography>
            <div className="flex flex-wrap gap-1">
              {staffApplication.periods.length > 0
                ? staffApplication.periods.map((period, index) => (
                    <PeriodBadge key={index} period={period} />
                  ))
                : '-'}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
              Pays souhaité(s)
            </Typography>
            <div className="flex flex-wrap gap-1">
              {staffApplication.countriesWishList.length > 0
                ? staffApplication.countriesWishList.map((country) => (
                    <CountryFlag key={country.name} code={country.code} />
                  ))
                : '-'}
            </div>
          </div>
        </div>
      </InnerBox>
      <div className="grid grid-cols-2 gap-4 grid-rows-1">
        <InnerBox header="Infomations professionnelles">
          <div className="grid grid-cols-2 px-4 gap-2 pt-4">
            <div className="flex flex-col gap-1">
              <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                Situation actuelle
              </Typography>
              <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                {staffApplication.currentSituation ?? '-'}
              </Typography>
            </div>
            <div className="flex flex-col gap-1">
              <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                Expérience de plus de 7 jours
              </Typography>
              <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                {staffApplication.hasSuperVisionExperienceOverSevenDays ? 'Oui' : 'Non'}
              </Typography>
            </div>

            {detailsType === 'full' && (
              <>
                <div className="flex flex-col gap-1">
                  <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                    Niveaux enseigné
                  </Typography>
                  <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                    {staffApplication.classLevels
                      .map((level) => t(schoolLevelTaughtEnumMapping[level]))
                      .join(', ')}
                  </Typography>
                </div>

                <div className="flex flex-col gap-1">
                  <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                    Nb d’année d’enseignement
                  </Typography>
                  <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                    {staffApplication.yearsOfExperience ?? '-'}
                  </Typography>
                </div>

                <div className="flex flex-col gap-1">
                  <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                    Membre de l’Éducation nationale
                  </Typography>
                  <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                    {staffApplication.memberOfNationalEducationStatus
                      ? nationalEducationStatusEnumMapping[
                          staffApplication.memberOfNationalEducationStatus
                        ]
                      : '-'}
                  </Typography>
                </div>
              </>
            )}

            <div className="flex flex-col gap-1">
              <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                Niveau d'études
              </Typography>
              <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                {staffApplication.levelOfEducation
                  ? educationLevelEnumMapping[staffApplication.levelOfEducation]
                  : '-'}
              </Typography>
            </div>
            <div className="flex flex-col gap-1">
              <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                Domaine d'étude
              </Typography>
              <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                {staffApplication.currentStudiedDomain ?? '-'}
              </Typography>
            </div>
            {detailsType === 'light' && (
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Diplôme en cours
                </Typography>
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {staffApplication.currentDiploma ?? '-'}
                </Typography>
              </div>
            )}
            <div className="flex flex-col gap-1">
              <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                Autres diplômes
              </Typography>
              <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                {staffApplication.otherDiploma ?? '-'}
              </Typography>
            </div>
          </div>
        </InnerBox>

        <div className="row-span-2">
          <InnerBox header="Infomations civilité">
            <div className="grid grid-cols-2 px-4 gap-2 pt-4">
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  NOM
                </Typography>
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {toFullName(staffApplication.firstName, staffApplication.lastName)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Genre
                </Typography>
                <Badge
                  variant={
                    genderOneLetterMapping[toGenderEnumValue(staffApplication.gender)].variant
                  }
                  text={genderOneLetterMapping[toGenderEnumValue(staffApplication.gender)].label}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  N° de téléphone
                </Typography>
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {formatPhoneNumber(staffApplication.phoneNumber)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Email
                </Typography>
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {staffApplication.email}
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Adresse
                </Typography>
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {staffApplication.address1}
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Ville
                </Typography>
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {staffApplication.city}
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Code postal
                </Typography>
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {staffApplication.postalCode}
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Pays
                </Typography>
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {staffApplication.country}
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Situation de famille
                </Typography>
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {staffApplication.maritalStatus
                    ? t(maritalStatusEnumMapping[staffApplication.maritalStatus])
                    : '-'}
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Nombre d'enfants
                </Typography>
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {staffApplication.numberOfChildren ?? '-'}
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Fait partie de l'Union Européenne
                </Typography>
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {staffApplication.partOfTheEuropeanUnion ? 'Oui' : 'Non'}
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Nationalité
                </Typography>
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {getNationalityLabel(staffApplication.nationality, nationalitiesList)}
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Âge
                </Typography>
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {`${DateFormatter.calculateAge(staffApplication.birthDate)} (${DateFormatter.DDMMYYYY(staffApplication.birthDate)})`}
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Lieu de naissance
                </Typography>
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {staffApplication.birthPlace}
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  N° de Séc. sociale
                </Typography>
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {staffApplication.socialSecurityNumber}
                </Typography>
              </div>
              <div className="flex flex-col gap-1">
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Assurance
                </Typography>
                <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                  {staffApplication.complementaryInsuranceName ?? '-'}
                </Typography>
              </div>
            </div>
          </InnerBox>
        </div>
        <InnerBox header="Langues" className="h-fit">
          <div className="flex flex-col px-4 gap-3 pt-4">
            {staffApplication.languagesTaught.map((language, index) => {
              const Icon = languageMapping[language.language].icon
              return (
                <div key={index} className="grid grid-cols-2 gap-1">
                  <div className="flex gap-3 items-center">
                    <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                      {languageMapping[language.language].label}
                    </Typography>
                    <Icon className={'size-6 rounded-full'} />
                  </div>
                  <Badge
                    text={languageLevelEnumMapping[language.level].label}
                    variant={languageLevelEnumMapping[language.level].variant}
                  />
                </div>
              )
            })}
          </div>
        </InnerBox>
      </div>

      <InnerBox header="Informations complémentaires">
        <div className="px-4 flex flex-col gap-4 pt-4">
          <div className="flex flex-col gap-2">
            <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
              Loisirs
            </Typography>
            <div className="flex flex-wrap gap-1">
              {staffApplication.hobbies.length > 0
                ? staffApplication.hobbies.map((hobby, index) => (
                    <Badge key={index} text={hobby.name} variant="warning" />
                  ))
                : '-'}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
              Expérience
            </Typography>
            <Typography
              variant="label-semibold"
              color={tailwindColors.black.DEFAULT}
              className="whitespace-pre-wrap break-words"
            >
              {staffApplication.experience ?? '-'}
            </Typography>
          </div>
          <div className="flex flex-col gap-2">
            <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
              Motivation
            </Typography>
            <Typography
              variant="label-semibold"
              color={tailwindColors.black.DEFAULT}
              className="whitespace-pre-wrap break-words"
            >
              {staffApplication.motivation ?? '-'}
            </Typography>
          </div>
        </div>
      </InnerBox>
    </div>
  )
}

export default StaffApplicationSummary
